import { ROLES, type Role } from '@/types/auth';

/**
 * Fine-grained permissions. Route/menu guards should prefer checking a
 * permission over a raw role wherever possible, so that role -> permission
 * mappings can evolve without touching every guard call site.
 */
export const PERMISSIONS = {
  USERS_VIEW: 'users:view',
  USERS_MANAGE: 'users:manage',
  CATEGORIES_VIEW: 'categories:view',
  CATEGORIES_MANAGE: 'categories:manage',
  QUIZZES_VIEW: 'quizzes:view',
  QUIZZES_MANAGE: 'quizzes:manage',
  NOTIFICATIONS_VIEW: 'notifications:view',
  NOTIFICATIONS_MANAGE: 'notifications:manage',
  DASHBOARD_VIEW: 'dashboard:view',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Single source of truth mapping each role to the permissions it holds.
 * ADMIN is granted the full permission set explicitly (rather than an
 * implicit wildcard) so new permissions must be a deliberate addition
 * here, not an accidental grant.
 */
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [ROLES.ADMIN]: Object.values(PERMISSIONS),
  [ROLES.CREATOR]: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CATEGORIES_VIEW,
    PERMISSIONS.CATEGORIES_MANAGE,
    PERMISSIONS.QUIZZES_VIEW,
    PERMISSIONS.QUIZZES_MANAGE,
    PERMISSIONS.NOTIFICATIONS_VIEW,
  ],
  [ROLES.USER]: [PERMISSIONS.DASHBOARD_VIEW],
};
